/* eslint-env browser */
// Security › Agent performance (wave 3, W2_Portal_Tables look): the team's
// figures as tiles, the leaderboard in the modern table, and the agent's
// evidence in a side panel (score breakdown, risk flags with their facts,
// when they work, call mix, recent calls and portal activity).
//
// AgentPerformance — admin-grade performance review for internal staff.
//
// This is the dashboard used to inform hire/promote/coach/fire
// decisions. Because those decisions are consequential, the design
// emphasizes EVIDENCE and TRANSPARENCY over slick visuals:
//
//   • Every headline score is broken down into its component metrics
//     with the source data one tap away.
//   • Scores are normalized against peer cohorts (rather than absolute
//     thresholds that go stale) and shown alongside the raw numbers
//     that produced them.
//   • Confidence flags surface agents with too little data to judge
//     fairly — so a sparse week doesn't get someone fired.
//   • Risk flags are listed with the exact timestamps / counts that
//     triggered them. Nothing inferred is presented without the
//     supporting fact.
//
// Data sources combined here (unchanged in wave 3; no new reads):
//   1. /driver_remarks (via Redux driverremarksdata) — call /
//      interaction records logged by call-center agents when they
//      contact drivers, asked for on open (bounded to the last 30 days)
//      and released on leave (fetchAllDriverRemarks /
//      stopFetchingAllDriverRemarks).
//   2. /staffActivityLogs (RTDB) — ONE bounded read from (now − window)
//      ordered by timestamp, again when the window changes or on Refresh.
//
// The two are cross-referenced by agent uid (driverRemarks.agentId ===
// staffActivityLogs.uid). An agent who shows up in only one source is
// still scored — just on the dimensions we have data for, with a
// reduced confidence band. Every metric and its formula is unchanged;
// every time shown is Addis Ababa on a 12-hour clock (owner rule).

import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FirebaseContext } from 'common';
import { ref, query, orderByChild, startAt, get } from 'firebase/database';
import moment from 'moment/min/moment-with-locales';
import { alpha } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { api } from 'common';
import { tokens as c } from '../../theme';
import { InlineMessage } from '../../components/feedback';
import DashboardCard from '../../components/DashboardCard';
import { formatAddisDateTime } from '../../common/exportUtils';
import { DataTable, DropChip, IconCircle, PageHeader, StatusChip } from '../../components/modern';

// ──────────────────────────────────────────────────────────────────────
//  Constants — scoring weights, thresholds, colours
// ──────────────────────────────────────────────────────────────────────

const CALL_CATEGORIES = ['Compliance', 'Booking', 'Credit', 'SOS', 'Complain', 'Other'];

// One colour per call category, from the theme.
const CATEGORY_COLORS = {
    Compliance: c.INFO_TEXT,
    Booking: c.SUCCESS_TEXT,
    Credit: c.WARNING_TEXT,
    SOS: c.DANGER_TEXT,
    Complain: c.ACCENT,
    Other: c.TEXT_3,
};
const CATEGORY_TONE = { Compliance: 'info', Booking: 'success', Credit: 'warning', SOS: 'error', Complain: 'accent', Other: 'neutral' };

// Pillar weights — must sum to 100 for the composite to land on a
// clean 0–100 scale. Productivity gets the biggest share because it's
// the most directly observable output. Engagement and Quality are
// equally weighted because either-or only paints half the picture
// (an agent can be online all day and do nothing, or be efficient
// for a handful of hours and well-rounded).
const SCORE_WEIGHTS = {
    productivity: 40,
    engagement: 30,
    quality: 30,
};

// Risk penalty cap. Even an agent with every red flag triggered loses
// at most this many points — we don't want a single anomaly (e.g. one
// IP change because they were traveling) to push someone from 75 → 0.
const MAX_RISK_PENALTY = 25;

// Per-flag penalties. These add up but are capped by MAX_RISK_PENALTY.
const RISK_PENALTIES = {
    newIp: 5,                    // appearance of an IP not in baseline
    multipleIpsInDay: 8,         // 2+ distinct IPs in a single day (account sharing?)
    offHoursActivity: 5,         // >20% of events outside core working hours
    inactivityGap: 7,            // ≥3 consecutive workdays with no activity
    singleCategoryDominance: 5,  // >80% calls one category (specialist or stuck?)
};

// Addis Ababa timezone — fixed UTC+3 (East Africa Time, no DST).
// Every timestamp displayed or bucketed by day/hour goes through
// inAddis() so it matches what the team actually experienced locally,
// regardless of the reviewing admin's browser timezone.
const ADDIS_OFFSET_MIN = 3 * 60;
const inAddis = (ts) => moment(ts).utcOffset(ADDIS_OFFSET_MIN);

// Core working hours per the company schedule: 9:00 am – 5:30 pm EAT.
// An event is "off-hours" if it falls outside this window.
const WORK_START_HOUR = 9;     // inclusive
const WORK_END_HOUR = 17;      // inclusive of 17:00–17:30
const WORK_END_MIN = 30;
const isOffHours = (hour, minute) => {
    if (hour < WORK_START_HOUR) return true;
    if (hour > WORK_END_HOUR) return true;
    if (hour === WORK_END_HOUR && minute > WORK_END_MIN) return true;
    return false;
};

// Confidence thresholds — below these we mark the agent's score as
// "low confidence" so the reviewer knows the number is fragile.
const MIN_CONFIDENT_DAYS = 5;
const MIN_CONFIDENT_EVENTS = 10;

// Tier bands — shown as a tone (icon + word, never colour alone).
const TIERS = [
    { min: 80, key: 'agent_perf_tier_outstanding', label: 'Outstanding', tone: 'success', color: c.SUCCESS_TEXT },
    { min: 60, key: 'agent_perf_tier_solid', label: 'Solid', tone: 'info', color: c.INFO_TEXT },
    { min: 40, key: 'agent_perf_tier_adequate', label: 'Adequate', tone: 'warning', color: c.WARNING_TEXT },
    { min: 0, key: 'agent_perf_tier_concerning', label: 'Concerning', tone: 'error', color: c.DANGER_TEXT },
];

const tierFor = (score) => TIERS.find((tier) => score >= tier.min) || TIERS[TIERS.length - 1];

// Engagement idle cap — single gap between same-session events capped
// at this when summing time-on-portal. Mirrors StaffActivity.js so the
// two screens report the same numbers.
const ENGAGEMENT_IDLE_CAP_MS = 5 * 60 * 1000;

// "9 am" / "5:30 pm" for an Addis Ababa hour (12-hour clock, owner rule).
const hourText = (h, m = 0) => `${h % 12 === 0 ? 12 : h % 12}${m ? `:${m < 10 ? `0${m}` : m}` : ''} ${h < 12 ? 'am' : 'pm'}`;

// ──────────────────────────────────────────────────────────────────────
//  Helpers
// ──────────────────────────────────────────────────────────────────────

const formatDuration = (ms) => {
    if (!ms || ms < 1000) return '0m';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return `${totalSec}s`;
};

// Compute engaged ms for one session's ascending timestamps.
const sessionEngagedMs = (timestampsAsc) => {
    if (!timestampsAsc || timestampsAsc.length === 0) return 0;
    if (timestampsAsc.length === 1) return 30 * 1000;
    let total = 0;
    for (let i = 1; i < timestampsAsc.length; i++) {
        const gap = timestampsAsc[i] - timestampsAsc[i - 1];
        if (gap > 0) total += Math.min(gap, ENGAGEMENT_IDLE_CAP_MS);
    }
    return total;
};

// Shannon entropy for category mix — used as the "well-roundedness"
// signal. Range 0–1 (1 = perfectly even across categories). An agent
// who only ever takes Credit calls scores ~0.25; one who covers all six
// categories evenly scores 1.0. Normalized against log2(N categories).
const categoryEntropy = (counts) => {
    const total = Object.values(counts).reduce((s, n) => s + n, 0);
    if (total === 0) return 0;
    const keys = Object.keys(counts).filter((k) => counts[k] > 0);
    if (keys.length <= 1) return 0;
    let h = 0;
    keys.forEach((k) => {
        const p = counts[k] / total;
        h -= p * Math.log2(p);
    });
    const maxH = Math.log2(CALL_CATEGORIES.length);
    return Math.max(0, Math.min(1, h / maxH));
};

// Normalize a raw value into 0–100 against the cohort's peak. If the
// peer max is 0 (nobody in the cohort did this thing) every agent
// scores 0 — which is the honest answer. Using peer-relative scoring
// avoids stale absolute thresholds; the bar moves with the team.
const normalize = (value, peerMax) => {
    if (!peerMax || peerMax <= 0) return 0;
    return Math.max(0, Math.min(100, (value / peerMax) * 100));
};

const initials = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]).join('').toUpperCase() || '?';
};

// ──────────────────────────────────────────────────────────────────────
//  Main component
// ──────────────────────────────────────────────────────────────────────

export default function AgentPerformance() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { database } = useContext(FirebaseContext);
    const remarksData = useSelector((state) => state.driverremarksdata);
    const usersdata = useSelector((state) => state.usersdata);
    const auth = useSelector((state) => state.auth);
    const { fetchAllDriverRemarks, stopFetchingAllDriverRemarks } = api;

    const isAdmin = auth?.profile?.usertype === 'admin';

    // Date window — default last 14 days. 14 strikes a balance: long
    // enough to be statistically meaningful, short enough to reflect
    // current performance (not someone's behavior 60 days ago).
    const [lookbackDays, setLookbackDays] = useState(14);
    const [staffLogs, setStaffLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(true);
    const [refreshTick, setRefreshTick] = useState(0);

    // UI state
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [search, setSearch] = useState('');

    // Pull call-remarks on mount — bounded to the last 30 days (the widest
    // Window option) instead of the whole /driver_remarks history.
    useEffect(() => {
        dispatch(fetchAllDriverRemarks(new Date(Date.now() - 30 * 86400000).toISOString()));
        return () => {
            dispatch(stopFetchingAllDriverRemarks());
        };
    }, [dispatch, fetchAllDriverRemarks, stopFetchingAllDriverRemarks]);

    // Read /staffActivityLogs scoped to the lookback window as a one-shot
    // get() — the previous live onValue re-downloaded the whole window on
    // every staff write. Re-runs when the window changes or on Refresh.
    useEffect(() => {
        if (!database || !isAdmin) return undefined;
        let cancelled = false;
        setLogsLoading(true);
        const since = Date.now() - (lookbackDays * 24 * 60 * 60 * 1000);
        const q = query(ref(database, 'staffActivityLogs'), orderByChild('timestamp'), startAt(since));
        get(q).then((snap) => {
            if (cancelled) return;
            const val = snap.val() || {};
            const arr = Object.keys(val).map((id) => Object.assign({}, val[id], { id }));
            arr.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
            setStaffLogs(arr);
            setLogsLoading(false);
        }).catch((err) => {
            console.error('[AgentPerformance] staffActivityLogs read failed:', err);
            if (!cancelled) setLogsLoading(false);
        });
        return () => { cancelled = true; };
    }, [database, isAdmin, lookbackDays, refreshTick]);

    // Range filtering for remarks (which Redux hands us across the
    // whole history — we narrow client-side to the lookback window).
    const filteredRemarks = useMemo(() => {
        if (!remarksData?.allRemarks) return [];
        const since = Date.now() - (lookbackDays * 24 * 60 * 60 * 1000);
        return remarksData.allRemarks.filter((r) => {
            const ts = new Date(r.timestamp).getTime();
            return ts >= since;
        });
    }, [remarksData?.allRemarks, lookbackDays]);

    // ── Aggregate per agent ────────────────────────────────────────
    // First pass: build the per-agent buckets from BOTH sources. An
    // agent appears here if they show up in remarks OR logs OR the
    // users node (so we surface staff who haven't been active too —
    // their score will be 0 / "no data", which is itself informative).
    const agentAggregates = useMemo(() => {
        const map = {};

        // Seed from users so we always include known staff. Mirrors
        // the STAFF_USER_TYPES list in staffActivityLogger.js.
        const STAFF_TYPES = ['finance', 'callcenteragent', 'callcentersupervisor'];
        const usersById = {};
        if (usersdata?.users) {
            usersdata.users.forEach((u) => {
                if (!u || !u.id) return;
                usersById[u.id] = u;
                if (STAFF_TYPES.indexOf(u.usertype) >= 0) {
                    map[u.id] = mkAgentBucket(u.id, {
                        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—',
                        email: u.email || '',
                        type: u.usertype,
                        image: u.profile_image || null,
                    });
                }
            });
        }

        // Layer in remarks.
        filteredRemarks.forEach((r) => {
            if (!r.agentId) return;
            if (!map[r.agentId]) {
                map[r.agentId] = mkAgentBucket(r.agentId, {
                    name: r.agentName || r.agentEmail || 'Unknown',
                    email: r.agentEmail || '',
                    type: '',
                    image: null,
                });
            }
            const a = map[r.agentId];
            a.calls.total++;
            a.calls.byCategory[r.callType] = (a.calls.byCategory[r.callType] || 0) + 1;
            a.calls.uniqueDrivers.add(r.driverId);
            const driverCount = a.calls.driverCounts[r.driverId] || 0;
            a.calls.driverCounts[r.driverId] = driverCount + 1;
            a.calls.list.push(r);
            // Day/hour/weekday are computed in Addis Ababa time (UTC+3)
            // so the metrics reflect what the team actually saw locally.
            const local = inAddis(r.timestamp);
            const dayKey = local.format('YYYY-MM-DD');
            a.activeDays.add(dayKey);
            // Hourly heatmap (0–23) × weekday (0–6). Each cell carries
            // the count of events in that bucket. Mon=1 in moment().day(),
            // we reindex to a Mon-first week downstream.
            const hour = local.hour();
            const dow = local.day();
            const idx = dow * 24 + hour;
            a.heatmap[idx] = (a.heatmap[idx] || 0) + 1;
        });

        // Layer in staff activity.
        staffLogs.forEach((log) => {
            if (!log.uid) return;
            if (!map[log.uid]) {
                map[log.uid] = mkAgentBucket(log.uid, {
                    name: log.userName || log.userEmail || 'Unknown',
                    email: log.userEmail || '',
                    type: log.userType || '',
                    image: usersById[log.uid]?.profile_image || null,
                });
            }
            const a = map[log.uid];
            if (log.eventType === 'page_view') a.activity.pageViews++;
            else if (log.eventType === 'download') a.activity.downloads++;
            else if (log.eventType === 'action') a.activity.actions++;
            a.activity.total++;
            if ((log.timestamp || 0) > a.lastSeen) a.lastSeen = log.timestamp || 0;
            // Day/hour bucketing in Addis Ababa local time so the
            // boundaries match the team's experience (e.g. an event
            // at 02:00 UTC = 05:00 Addis still counts as off-hours).
            const local = inAddis(log.timestamp);
            const dayKey = local.format('YYYY-MM-DD');
            a.activeDays.add(dayKey);

            // Sessions for engagement time.
            const sid = log.sessionId || 'no-session';
            if (!a.sessions[sid]) a.sessions[sid] = [];
            a.sessions[sid].push(log.timestamp || 0);

            // IP tracking.
            if (log.clientIp) {
                a.ips.all.add(log.clientIp);
                if (!a.ips.byDay[dayKey]) a.ips.byDay[dayKey] = new Set();
                a.ips.byDay[dayKey].add(log.clientIp);
            }

            // Off-hours = outside core 09:00–17:30 EAT.
            const hour = local.hour();
            const minute = local.minute();
            if (isOffHours(hour, minute)) a.offHoursEvents++;

            // Update the heatmap from activity events too — gives a
            // fuller picture of WHEN the agent is at the keyboard, not
            // just when they're logging calls.
            const dow = local.day();
            const idx = dow * 24 + hour;
            a.heatmap[idx] = (a.heatmap[idx] || 0) + 1;

            a.activity.list.push(log);
        });

        // Second pass: collapse sets, compute derived metrics.
        Object.values(map).forEach((a) => {
            // Time on portal — sum capped intra-session gaps.
            let totalMs = 0;
            const sessionIds = Object.keys(a.sessions);
            sessionIds.forEach((sid) => {
                const ts = a.sessions[sid].sort((x, y) => x - y);
                totalMs += sessionEngagedMs(ts);
            });
            a.engagement.timeOnPortalMs = totalMs;
            a.engagement.sessionCount = sessionIds.length;
            a.engagement.activeDays = a.activeDays.size;
            a.engagement.avgSessionMs = sessionIds.length > 0
                ? Math.round(totalMs / sessionIds.length) : 0;

            // Quality metrics.
            a.quality.uniqueDrivers = a.calls.uniqueDrivers.size;
            a.quality.categoryEntropy = categoryEntropy(a.calls.byCategory);
            const repeats = Object.values(a.calls.driverCounts).filter((c) => c > 1).length;
            a.quality.repeatDrivers = repeats;
            a.quality.repeatDriverPct = a.quality.uniqueDrivers > 0
                ? (repeats / a.quality.uniqueDrivers) * 100 : 0;
            a.quality.sensitiveCases =
                (a.calls.byCategory['SOS'] || 0) + (a.calls.byCategory['Complain'] || 0);

            // Productivity composite (raw — peer-normalized below).
            a.productivity = {
                calls: a.calls.total,
                drivers: a.quality.uniqueDrivers,
                actions: a.activity.actions,
                downloads: a.activity.downloads,
            };

            // Risk flags — concrete, with the evidence behind them.
            a.risk.flags = [];
            // Multiple IPs in a day
            const maxIpsInOneDay = Math.max(0, ...Object.values(a.ips.byDay).map((s) => s.size));
            if (maxIpsInOneDay >= 2) {
                a.risk.flags.push({
                    code: 'multipleIpsInDay',
                    severity: 'high',
                    label: `${maxIpsInOneDay} distinct IPs in one day`,
                    params: { count: maxIpsInOneDay },
                    detail: 'Possible account sharing or impossible-travel signal. Verify with the agent.',
                });
            }
            // Off-hours dominance — outside the core 09:00–17:30 EAT window.
            const offRatio = a.activity.total > 0 ? a.offHoursEvents / a.activity.total : 0;
            if (offRatio > 0.2 && a.activity.total >= 10) {
                a.risk.flags.push({
                    code: 'offHoursActivity',
                    severity: 'medium',
                    label: `${Math.round(offRatio * 100)}% of activity outside core hours (9:00 am – 5:30 pm Addis Ababa time)`,
                    params: { pct: Math.round(offRatio * 100) },
                    detail: 'Unusual work schedule. Could be coverage rotation, could be unauthorized access.',
                });
            }
            // Inactivity gap
            const sortedDays = Array.from(a.activeDays).sort();
            let maxGap = 0;
            for (let i = 1; i < sortedDays.length; i++) {
                const gap = moment(sortedDays[i]).diff(moment(sortedDays[i - 1]), 'days');
                if (gap > maxGap) maxGap = gap;
            }
            if (maxGap >= 3) {
                a.risk.flags.push({
                    code: 'inactivityGap',
                    severity: 'medium',
                    label: `${maxGap}-day inactivity gap in period`,
                    params: { count: maxGap },
                    detail: 'Could be approved leave, but worth confirming.',
                });
            }
            // Single-category dominance
            if (a.calls.total >= 20) {
                const topCount = Math.max(0, ...Object.values(a.calls.byCategory));
                const topShare = topCount / a.calls.total;
                if (topShare > 0.8) {
                    a.risk.flags.push({
                        code: 'singleCategoryDominance',
                        severity: 'low',
                        label: `${Math.round(topShare * 100)}% of calls one category`,
                        params: { pct: Math.round(topShare * 100) },
                        detail: 'Highly specialized — fine if intentional, worth checking otherwise.',
                    });
                }
            }
            // We can't compute "new IP" here without a baseline window
            // — that's covered by StaffActivity's existing IP panel.
            // The other four flags are enough signal for performance.
        });

        return Object.values(map);
    }, [filteredRemarks, staffLogs, usersdata?.users]);

    // ── Peer normalization + composite scoring ─────────────────────
    const scoredAgents = useMemo(() => {
        if (agentAggregates.length === 0) return [];

        // Compute peer-maxes ONCE per render.
        const peer = {
            calls: 1, drivers: 1, actions: 1, downloads: 1,
            timeMs: 1, activeDays: 1, sessions: 1,
            sensitive: 1,
        };
        agentAggregates.forEach((a) => {
            peer.calls    = Math.max(peer.calls,    a.productivity.calls);
            peer.drivers  = Math.max(peer.drivers,  a.productivity.drivers);
            peer.actions  = Math.max(peer.actions,  a.productivity.actions);
            peer.downloads = Math.max(peer.downloads, a.productivity.downloads);
            peer.timeMs   = Math.max(peer.timeMs,   a.engagement.timeOnPortalMs);
            peer.activeDays = Math.max(peer.activeDays, a.engagement.activeDays);
            peer.sessions = Math.max(peer.sessions, a.engagement.sessionCount);
            peer.sensitive = Math.max(peer.sensitive, a.quality.sensitiveCases);
        });

        return agentAggregates.map((a) => {
            // ── Productivity (out of 40) ────────────────────────────
            // Equal sub-weight across the four observable outputs.
            const pCalls     = normalize(a.productivity.calls, peer.calls);
            const pDrivers   = normalize(a.productivity.drivers, peer.drivers);
            const pActions   = normalize(a.productivity.actions, peer.actions);
            const pDownloads = normalize(a.productivity.downloads, peer.downloads);
            const productivity100 = (pCalls + pDrivers + pActions + pDownloads) / 4;
            const productivity = (productivity100 / 100) * SCORE_WEIGHTS.productivity;

            // ── Engagement (out of 30) ──────────────────────────────
            // Weight time-on-portal most heavily (the actual signal);
            // active days + session count are corroborating evidence.
            const eTime    = normalize(a.engagement.timeOnPortalMs, peer.timeMs);
            const eDays    = normalize(a.engagement.activeDays, Math.min(peer.activeDays, lookbackDays));
            const eSessions = normalize(a.engagement.sessionCount, peer.sessions);
            const engagement100 = (eTime * 0.5) + (eDays * 0.3) + (eSessions * 0.2);
            const engagement = (engagement100 / 100) * SCORE_WEIGHTS.engagement;

            // ── Quality (out of 30) ─────────────────────────────────
            // Entropy is naturally 0–1, scale to 100. Repeat-driver
            // pct caps at 60 (above which it implies they're stuck on
            // a small driver set — could be good or bad, so we don't
            // over-reward). Sensitive cases reward agents who handle
            // SOS/Complain (high-stakes touchpoints).
            const qEntropy   = a.quality.categoryEntropy * 100;
            const qRepeat    = Math.min(100, (a.quality.repeatDriverPct / 60) * 100);
            const qSensitive = normalize(a.quality.sensitiveCases, peer.sensitive);
            const quality100 = (qEntropy * 0.4) + (qRepeat * 0.3) + (qSensitive * 0.3);
            const quality = (quality100 / 100) * SCORE_WEIGHTS.quality;

            // ── Risk penalty (capped) ───────────────────────────────
            let penaltyRaw = 0;
            a.risk.flags.forEach((f) => {
                penaltyRaw += RISK_PENALTIES[f.code] || 0;
            });
            const penalty = Math.min(MAX_RISK_PENALTY, penaltyRaw);

            // ── Composite ───────────────────────────────────────────
            const rawScore = productivity + engagement + quality - penalty;
            const score = Math.max(0, Math.min(100, Math.round(rawScore)));

            // Confidence — flag fragile scores so reviewers don't
            // over-index on agents with too little data.
            const totalEvents = a.calls.total + a.activity.total;
            const lowConfidence =
                a.engagement.activeDays < MIN_CONFIDENT_DAYS
                || totalEvents < MIN_CONFIDENT_EVENTS;

            return Object.assign({}, a, {
                score,
                breakdown: {
                    productivity: Math.round(productivity),
                    productivityComponents: { pCalls, pDrivers, pActions, pDownloads },
                    engagement: Math.round(engagement),
                    engagementComponents: { eTime, eDays, eSessions },
                    quality: Math.round(quality),
                    qualityComponents: { qEntropy, qRepeat, qSensitive },
                    penalty: Math.round(penalty),
                },
                lowConfidence,
                peer,
            });
        }).sort((a, b) => b.score - a.score);
    }, [agentAggregates, lookbackDays]);

    // Filter by search term.
    const visibleAgents = useMemo(() => {
        if (!search.trim()) return scoredAgents;
        const q = search.toLowerCase();
        return scoredAgents.filter((a) =>
            (a.name || '').toLowerCase().includes(q) ||
            (a.email || '').toLowerCase().includes(q)
        );
    }, [scoredAgents, search]);

    // Headline KPIs across the whole team — for the top strip.
    const teamKpi = useMemo(() => {
        const totalCalls = scoredAgents.reduce((s, a) => s + a.calls.total, 0);
        const totalDrivers = new Set();
        scoredAgents.forEach((a) => { a.calls.uniqueDrivers.forEach((id) => totalDrivers.add(id)); });
        const totalTimeMs = scoredAgents.reduce((s, a) => s + a.engagement.timeOnPortalMs, 0);
        const activeCount = scoredAgents.filter((a) => a.activeDays.size > 0).length;
        const avgScore = scoredAgents.length > 0
            ? Math.round(scoredAgents.reduce((s, a) => s + a.score, 0) / scoredAgents.length)
            : 0;
        const flaggedCount = scoredAgents.filter((a) => a.risk.flags.length > 0).length;
        return { totalCalls, drivers: totalDrivers.size, totalTimeMs, activeCount, avgScore, flaggedCount };
    }, [scoredAgents]);

    const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });

    if (!isAdmin) {
        return (
            <InlineMessage kind="error" sx={{ my: 2 }}>
                {L('staff_activity_admin_only', 'Admin access required.')}
            </InlineMessage>
        );
    }

    // Overall rank by score (kept while the list is searched).
    const rankOf = new Map(scoredAgents.map((a, i) => [a.uid, i + 1]));
    const windowLabel = (d) => t('staff_activity_last_days', { count: d, defaultValue: 'Last {{count}} days' });
    const tierLabel = (score) => {
        const tier = tierFor(score);
        return L(tier.key, tier.label);
    };
    const anyLoading = logsLoading || !!remarksData?.allRemarksLoading;
    const windowOptions = [7, 14, 30].map((d) => ({ value: d, label: windowLabel(d) }));

    const columns = [
        {
            id: 'rank',
            title: '#',
            width: 56,
            hideable: false,
            searchable: false,
            sortValue: (a) => rankOf.get(a.uid),
            render: (a) =>
                rankOf.get(a.uid) === 1 ? (
                    <EmojiEventsOutlinedIcon titleAccess="1" sx={{ color: c.AMBER_TEXT, fontSize: 20, verticalAlign: 'middle' }} />
                ) : (
                    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', color: c.INK_2 }}>{rankOf.get(a.uid)}</Box>
                ),
        },
        {
            id: 'agent',
            title: L('agent', 'Agent'),
            field: 'name',
            render: (a) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 200 }}>
                    <Avatar src={a.image || undefined} alt="" sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: c.SURFACE_ALT, color: c.INK_2 }}>
                        {initials(a.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{a.name}</Box>
                        <Box sx={{ fontSize: 12.5, color: c.TEXT_3, overflowWrap: 'anywhere' }}>{a.email}</Box>
                    </Box>
                </Box>
            ),
        },
        {
            id: 'score',
            title: L('agent_perf_score', 'Score'),
            defaultSort: 'desc',
            sortValue: (a) => a.score,
            render: (a) => {
                const tier = tierFor(a.score);
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                        <StatusChip tone={tier.tone} label={`${a.score} · ${tierLabel(a.score)}`} sx={{ fontVariantNumeric: 'tabular-nums' }} />
                        {a.lowConfidence ? (
                            <Tooltip
                                title={L(
                                    'agent_perf_low_confidence_hint',
                                    'Too few data points to score with high confidence. Wait for more events or extend the window.'
                                )}
                            >
                                <Box component="span" tabIndex={0} sx={{ fontSize: 12, fontWeight: 600, color: c.WARNING_TEXT, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <WarningAmberRoundedIcon aria-hidden="true" sx={{ fontSize: 14 }} />
                                    {L('agent_perf_low_confidence', 'Low confidence')}
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                );
            },
        },
        { id: 'calls', title: L('calls', 'Calls'), type: 'number', sortValue: (a) => a.calls.total, render: (a) => a.calls.total },
        { id: 'drivers', title: L('drivers', 'Drivers'), type: 'number', sortValue: (a) => a.quality.uniqueDrivers, render: (a) => a.quality.uniqueDrivers },
        { id: 'actions', title: L('actions', 'Actions'), type: 'number', sortValue: (a) => a.activity.actions, render: (a) => a.activity.actions },
        {
            id: 'portalTime',
            title: L('agent_perf_portal_time', 'Portal time'),
            type: 'number',
            sortValue: (a) => a.engagement.timeOnPortalMs,
            render: (a) => formatDuration(a.engagement.timeOnPortalMs),
        },
        { id: 'activeDays', title: L('agent_perf_active_days', 'Active days'), type: 'number', sortValue: (a) => a.engagement.activeDays, render: (a) => a.engagement.activeDays },
        {
            id: 'coverage',
            title: L('agent_perf_coverage', 'Coverage'),
            sortable: false,
            searchable: false,
            minWidth: 150,
            render: (a) => <CategoryStrip counts={a.calls.byCategory} total={a.calls.total} />,
        },
        {
            id: 'flags',
            title: L('agent_perf_flags', 'Flags'),
            sortValue: (a) => a.risk.flags.length,
            render: (a) =>
                a.risk.flags.length > 0 ? (
                    <StatusChip tone="error" icon={<WarningAmberRoundedIcon />} label={String(a.risk.flags.length)} />
                ) : (
                    <StatusChip tone="success" icon={<CheckRoundedIcon />} label={L('agent_perf_clean', 'Clean')} />
                ),
        },
    ];

    const avgTier = tierFor(teamKpi.avgScore);

    return (
        <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Window and refresh */}
            <PageHeader
                secondaryActions={[
                    { key: 'refresh', label: L('refresh', 'Refresh'), icon: <RefreshRoundedIcon />, onClick: () => setRefreshTick((n) => n + 1), disabled: logsLoading },
                ]}
            >
                <DropChip label={L('staff_activity_window', 'Window')} value={lookbackDays} options={windowOptions} onChange={(v) => setLookbackDays(Number(v))} allValue={14} />
            </PageHeader>

            {/* Team figures */}
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' } }}>
                <DashboardCard
                    icon={<InsightsOutlinedIcon />}
                    hue="accent"
                    title={L('agent_perf_avg_score', 'Average score')}
                    value={
                        <Box component="span" sx={{ color: avgTier.color }}>
                            {teamKpi.avgScore}
                            <Box component="span" sx={{ fontSize: 14, fontWeight: 500, color: c.TEXT_3 }}>
                                {' / 100'}
                            </Box>
                        </Box>
                    }
                    caption={tierLabel(teamKpi.avgScore)}
                />
                <DashboardCard icon={<PhoneInTalkOutlinedIcon />} hue="info" title={L('agent_perf_calls_logged', 'Calls logged')} value={String(teamKpi.totalCalls)} />
                <DashboardCard icon={<PersonOutlineRoundedIcon />} hue="success" title={L('agent_perf_drivers_reached', 'Drivers reached')} value={String(teamKpi.drivers)} />
                <DashboardCard icon={<ScheduleRoundedIcon />} hue="info" title={L('agent_perf_total_portal_time', 'Total portal time')} value={formatDuration(teamKpi.totalTimeMs)} />
                <DashboardCard icon={<SupportAgentOutlinedIcon />} hue="neutral" title={L('active_agents', 'Active agents')} value={String(teamKpi.activeCount)} />
                <DashboardCard
                    icon={<FlagOutlinedIcon />}
                    hue={teamKpi.flaggedCount > 0 ? 'danger' : 'neutral'}
                    title={L('agent_perf_with_flags', 'With risk flags')}
                    value={String(teamKpi.flaggedCount)}
                />
            </Box>

            {/* Leaderboard */}
            <DataTable
                title={L('agent_perf_leaderboard', 'Agent leaderboard')}
                rows={visibleAgents}
                columns={columns}
                rowKey="uid"
                loading={anyLoading && scoredAgents.length === 0}
                refreshing={anyLoading && scoredAgents.length > 0}
                countText={({ shown, query: q, loading: busy }) =>
                    busy
                        ? L('loading', 'Loading…')
                        : q
                            ? L('table_count_shown', '{{shown}} of {{total}}', { shown, total: scoredAgents.length })
                            : t('agent_perf_count', { count: scoredAgents.length, n: scoredAgents.length, defaultValue_one: '{{n}} agent', defaultValue_other: '{{n}} agents', defaultValue: '{{n}} agents' })
                }
                subtitle={`${windowLabel(lookbackDays)} · ${L('agent_perf_note', 'Scores compare each agent with the others shown. Click a row for the breakdown.')}`}
                searchText={search}
                onSearchChange={setSearch}
                serverSearch
                searchPlaceholder={L('agent_perf_search', 'Search agents…')}
                onRowClick={(a) => setSelectedAgent(a)}
                rowLabel={(a) => a.name}
                prefsKey="security_agent_performance"
                minWidth={1180}
                emptyState={({ query: q }) =>
                    q
                        ? {
                            icon: <SupportAgentOutlinedIcon />,
                            title: L('agent_perf_no_match', 'No agents match.'),
                            actionLabel: L('clear_search', 'Clear search'),
                            onAction: () => setSearch(''),
                        }
                        : { icon: <SupportAgentOutlinedIcon />, title: L('agent_perf_no_agents', 'No agents to score yet.') }
                }
            />

            {/* Side panel with full agent detail */}
            <AgentDetailDrawer agent={selectedAgent} lookbackDays={lookbackDays} onClose={() => setSelectedAgent(null)} />
        </Box>
    );
}

// ──────────────────────────────────────────────────────────────────────
//  Subcomponents
// ──────────────────────────────────────────────────────────────────────

// Risk flags in words (with the facts), in the reviewer's language.
const FLAG_TEXT = {
    multipleIpsInDay: {
        key: 'agent_perf_flag_ips',
        label: '{{count}} distinct IPs in one day',
        detailKey: 'agent_perf_flag_ips_detail',
        detail: 'Possible account sharing or impossible-travel signal. Verify with the agent.',
    },
    offHoursActivity: {
        key: 'agent_perf_flag_off_hours',
        label: '{{pct}}% of activity outside core hours (9:00 am – 5:30 pm)',
        detailKey: 'agent_perf_flag_off_hours_detail',
        detail: 'Unusual work schedule. Could be coverage rotation, could be unauthorized access.',
    },
    inactivityGap: {
        key: 'agent_perf_flag_gap',
        label: '{{count}}-day inactivity gap in the period',
        detailKey: 'agent_perf_flag_gap_detail',
        detail: 'Could be approved leave, but worth confirming.',
    },
    singleCategoryDominance: {
        key: 'agent_perf_flag_one_category',
        label: '{{pct}}% of calls in one category',
        detailKey: 'agent_perf_flag_one_category_detail',
        detail: 'Highly specialized — fine if intentional, worth checking otherwise.',
    },
};

const flagText = (f, t) => {
    const m = FLAG_TEXT[f.code];
    if (!m) return { title: f.label, detail: f.detail };
    return { title: t(m.key, { ...(f.params || {}), defaultValue: m.label }), detail: t(m.detailKey, { defaultValue: m.detail }) };
};

const categoryName = (cat, t) => t(String(cat).toLowerCase(), { defaultValue: cat });

// Inline stacked bar showing call category mix at a glance.
function CategoryStrip({ counts, total }) {
    const { t } = useTranslation();
    if (!total) return <Box component="span" sx={{ color: c.TEXT_3 }}>—</Box>;
    return (
        <Box sx={{ display: 'flex', width: '100%', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: c.SURFACE_ALT }}>
            {CALL_CATEGORIES.map((cat) => {
                const v = counts[cat] || 0;
                if (v === 0) return null;
                const pct = (v / total) * 100;
                return (
                    <Tooltip key={cat} title={`${categoryName(cat, t)}: ${v} (${Math.round(pct)}%)`}>
                        <Box sx={{ width: `${pct}%`, bgcolor: CATEGORY_COLORS[cat] }} />
                    </Tooltip>
                );
            })}
        </Box>
    );
}

function SectionTitle({ children }) {
    return (
        <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, color: c.INK, mb: 1 }}>
            {children}
        </Typography>
    );
}

// Agent detail side panel — slides in from the right. Shows the score
// breakdown, heatmap, risk flags, recent calls and recent activity.
function AgentDetailDrawer({ agent, lookbackDays, onClose }) {
    const { t, i18n } = useTranslation();
    const settings = useSelector((state) => state.settingsdata.settings);
    if (!agent) return null;
    const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
    const allowPrivate = !!(settings && settings.AllowCriticalEditsAdmin);
    const tier = tierFor(agent.score);
    const b = agent.breakdown;
    const sub = (key, dflt) => L(key, dflt);
    const card = { p: 2, mb: 2.5 };

    return (
        <Drawer anchor={i18n.dir() === 'rtl' ? 'left' : 'right'} open={!!agent} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 580 }, p: 0, bgcolor: c.SCREEN_BG } }}>
            <Box dir={i18n.dir()} sx={{ p: 2, borderBottom: `1px solid ${c.OUTLINE}`, position: 'sticky', top: 0, bgcolor: c.WHITE, zIndex: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Avatar src={agent.image || undefined} alt="" sx={{ width: 56, height: 56, fontSize: 18, fontWeight: 700, bgcolor: c.SURFACE_ALT, color: c.INK_2 }}>
                            {initials(agent.name)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700, color: c.INK, overflowWrap: 'anywhere' }}>
                                {agent.name}
                            </Typography>
                            <Typography sx={{ fontSize: 13.5, color: c.INK_2, overflowWrap: 'anywhere' }}>
                                {agent.email} · {agent.type ? t(agent.type, { defaultValue: agent.type }) : L('agent_perf_staff', 'staff')}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} aria-label={L('close_dialog', 'Close')}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Box>
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ px: 2, py: 1, borderRadius: '12px', bgcolor: alpha(tier.color, 0.1), minWidth: 96 }}>
                        <Typography sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1, color: tier.color, fontVariantNumeric: 'tabular-nums' }}>{agent.score}</Typography>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tier.color }}>{L(tier.key, tier.label)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ScoreBreakdownBar b={b} />
                    </Box>
                </Box>
                {agent.lowConfidence ? (
                    <InlineMessage kind="warning" sx={{ mt: 2 }}>
                        {L(
                            'agent_perf_low_confidence_body',
                            'Low confidence: this agent has only {{days}} active day(s) and {{events}} event(s) in the window. Wait for more data before judging.',
                            { days: agent.engagement.activeDays, events: agent.calls.total + agent.activity.total }
                        )}
                    </InlineMessage>
                ) : null}
            </Box>

            <Box dir={i18n.dir()} sx={{ p: 2 }}>
                {/* Risk flags first — most actionable. */}
                {agent.risk.flags.length > 0 ? (
                    <Box sx={{ mb: 2.5 }}>
                        <SectionTitle>{L('agent_perf_risk_flags', 'Risk flags')}</SectionTitle>
                        {agent.risk.flags.map((f, i) => {
                            const text = flagText(f, t);
                            return (
                                <InlineMessage
                                    key={`${f.code}-${i}`}
                                    kind={f.severity === 'high' ? 'error' : f.severity === 'medium' ? 'warning' : 'info'}
                                    title={text.title}
                                    sx={{ mb: 1 }}
                                >
                                    {text.detail}
                                </InlineMessage>
                            );
                        })}
                    </Box>
                ) : null}

                {/* Score breakdown — the math. */}
                <SectionTitle>{L('agent_perf_breakdown', 'Score breakdown')}</SectionTitle>
                <Paper sx={{ px: 2, py: 0.5, mb: 2.5 }}>
                    <BreakdownRow label={L('agent_perf_productivity', 'Productivity')} of={SCORE_WEIGHTS.productivity} got={b.productivity} color={c.INFO_TEXT}>
                        <Sub label={sub('calls', 'Calls')} v={agent.productivity.calls} peer={agent.peer.calls} />
                        <Sub label={sub('drivers', 'Drivers')} v={agent.productivity.drivers} peer={agent.peer.drivers} />
                        <Sub label={sub('actions', 'Actions')} v={agent.productivity.actions} peer={agent.peer.actions} />
                        <Sub label={sub('agent_perf_downloads', 'Downloads')} v={agent.productivity.downloads} peer={agent.peer.downloads} />
                    </BreakdownRow>
                    <BreakdownRow label={L('agent_perf_engagement', 'Engagement')} of={SCORE_WEIGHTS.engagement} got={b.engagement} color={c.ACCENT_TEXT}>
                        <Sub label={sub('staff_activity_time_on_portal', 'Time on portal')} v={formatDuration(agent.engagement.timeOnPortalMs)} />
                        <Sub label={sub('agent_perf_active_days', 'Active days')} v={`${agent.engagement.activeDays} / ${lookbackDays}`} />
                        <Sub label={sub('staff_activity_sessions_title', 'Sessions')} v={agent.engagement.sessionCount} />
                        <Sub label={sub('agent_perf_avg_session', 'Avg session')} v={formatDuration(agent.engagement.avgSessionMs)} />
                    </BreakdownRow>
                    <BreakdownRow label={L('agent_perf_quality', 'Quality')} of={SCORE_WEIGHTS.quality} got={b.quality} color={c.SUCCESS_TEXT}>
                        <Sub label={sub('agent_perf_category_coverage', 'Category coverage')} v={`${Math.round(agent.quality.categoryEntropy * 100)}%`} />
                        <Sub label={sub('agent_perf_repeat_drivers', 'Repeat drivers')} v={`${agent.quality.repeatDrivers} (${Math.round(agent.quality.repeatDriverPct)}%)`} />
                        <Sub label={sub('agent_perf_sensitive', 'SOS / complaints handled')} v={agent.quality.sensitiveCases} />
                    </BreakdownRow>
                    {b.penalty > 0 ? (
                        <BreakdownRow label={L('agent_perf_risk_penalty', 'Risk penalty')} of={MAX_RISK_PENALTY} got={-b.penalty} color={c.DANGER_TEXT} isPenalty>
                            <Typography sx={{ fontSize: 12.5, color: c.INK_2 }}>
                                {t('agent_perf_flags_contributed', {
                                    count: agent.risk.flags.length,
                                    defaultValue_one: '{{count}} flag contributed.',
                                    defaultValue_other: '{{count}} flags contributed.',
                                    defaultValue: '{{count}} flags contributed.',
                                })}
                            </Typography>
                        </BreakdownRow>
                    ) : null}
                </Paper>

                {/* Activity heatmap. */}
                <SectionTitle>{L('agent_perf_when_they_work', 'When they work (Addis Ababa time)')}</SectionTitle>
                <Paper sx={{ ...card, overflowX: 'auto' }}>
                    <Heatmap data={agent.heatmap} />
                </Paper>

                {/* Call category mix. */}
                <SectionTitle>{L('agent_perf_call_mix', 'Call mix')}</SectionTitle>
                <Paper sx={card}>
                    {agent.calls.total === 0 ? (
                        <Typography sx={{ color: c.TEXT_3, fontSize: 14 }}>{L('agent_perf_no_calls', 'No calls logged in this window.')}</Typography>
                    ) : (
                        <Box>
                            <CategoryStrip counts={agent.calls.byCategory} total={agent.calls.total} />
                            <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {CALL_CATEGORIES.map((cat) => {
                                    const v = agent.calls.byCategory[cat] || 0;
                                    const pct = agent.calls.total > 0 ? Math.round((v / agent.calls.total) * 100) : 0;
                                    return (
                                        <Box key={cat} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Box aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: CATEGORY_COLORS[cat] }} />
                                            <Typography component="span" sx={{ fontSize: 13, color: c.INK_2 }}>
                                                {categoryName(cat, t)}{' '}
                                                <Box component="span" sx={{ fontWeight: 700, color: c.INK }}>
                                                    {v}
                                                </Box>{' '}
                                                ({pct}%)
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </Paper>

                {/* Recent calls and recent activity — evidence. */}
                <SectionTitle>{L('agent_perf_recent_calls', 'Recent calls')}</SectionTitle>
                <Paper sx={card}>
                    {agent.calls.list.length === 0 ? (
                        <Typography sx={{ color: c.TEXT_3, fontSize: 14 }}>{L('agent_perf_none', 'None.')}</Typography>
                    ) : (
                        agent.calls.list.slice(0, 10).map((r, i) => (
                            <Box key={r.id || i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1, borderBottom: `1px solid ${c.OUTLINE}`, '&:last-of-type': { borderBottom: 0 } }}>
                                <StatusChip tone={CATEGORY_TONE[r.callType] || 'neutral'} label={categoryName(r.callType || 'Other', t)} sx={{ height: 22, fontSize: 12 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 13.5, color: c.INK, overflowWrap: 'anywhere' }}>
                                        {r.driverName || (r.driverMobile ? (allowPrivate ? r.driverMobile : t('hidden_demo')) : r.driverId)}
                                        {r.remark ? ` — ${r.remark}` : ''}
                                    </Typography>
                                </Box>
                                <Typography component="span" sx={{ fontSize: 12.5, color: c.TEXT_3, whiteSpace: 'nowrap' }}>
                                    {formatAddisDateTime(r.timestamp)}
                                </Typography>
                            </Box>
                        ))
                    )}
                    {agent.calls.list.length > 10 ? (
                        <Typography sx={{ fontSize: 13, color: c.TEXT_3, mt: 1 }}>
                            {L('agent_perf_first_10', 'Showing the first 10 of {{count}}.', { count: agent.calls.list.length })}
                        </Typography>
                    ) : null}
                </Paper>

                <SectionTitle>{L('agent_perf_recent_activity', 'Recent portal activity')}</SectionTitle>
                <Paper sx={card}>
                    {agent.activity.list.length === 0 ? (
                        <Typography sx={{ color: c.TEXT_3, fontSize: 14 }}>{L('agent_perf_none', 'None.')}</Typography>
                    ) : (
                        agent.activity.list
                            .slice(-10)
                            .reverse()
                            .map((log) => {
                                const Icon = log.eventType === 'page_view' ? VisibilityOutlinedIcon : log.eventType === 'download' ? FileDownloadOutlinedIcon : TouchAppOutlinedIcon;
                                const hue = log.eventType === 'page_view' ? 'info' : log.eventType === 'download' ? 'amber' : 'accent';
                                return (
                                    <Box key={log.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.75, borderBottom: `1px solid ${c.OUTLINE}`, '&:last-of-type': { borderBottom: 0 } }}>
                                        <IconCircle icon={<Icon />} hue={hue} size={28} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: c.INK, overflowWrap: 'anywhere' }}>
                                                {log.action}{' '}
                                                <Box component="span" sx={{ color: c.TEXT_3, fontWeight: 400 }}>
                                                    · {log.page}
                                                </Box>
                                            </Typography>
                                            {log.target && log.target.label ? (
                                                <Typography sx={{ fontSize: 12.5, color: c.TEXT_3, overflowWrap: 'anywhere' }}>
                                                    {log.target.type}: {log.target.label}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                        <Typography component="span" sx={{ fontSize: 12.5, color: c.TEXT_3, whiteSpace: 'nowrap' }}>
                                            {formatAddisDateTime(log.timestamp)}
                                        </Typography>
                                    </Box>
                                );
                            })
                    )}
                </Paper>
            </Box>
        </Drawer>
    );
}

// Single pillar row of the breakdown. Got/of with a progress bar plus
// children = the contributing sub-metrics.
function BreakdownRow({ label, of, got, color, isPenalty, children }) {
    const pct = isPenalty ? Math.min(100, (Math.abs(got) / of) * 100) : Math.min(100, (got / of) * 100);
    return (
        <Box sx={{ py: 1.5, borderBottom: `1px solid ${c.OUTLINE}`, '&:last-child': { borderBottom: 'none' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: c.INK }}>{label}</Typography>
                <Typography sx={{ color, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {got > 0 ? '+' : ''}
                    {got}{' '}
                    <Box component="span" sx={{ color: c.TEXT_3, fontWeight: 400, fontSize: 12 }}>
                        / {of}
                    </Box>
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={pct}
                aria-label={`${label}: ${got} / ${of}`}
                sx={{ height: 6, borderRadius: 3, mb: 1, bgcolor: c.SURFACE_ALT, '& .MuiLinearProgress-bar': { bgcolor: color } }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>{children}</Box>
        </Box>
    );
}

function Sub({ label, v, peer }) {
    const { t } = useTranslation();
    return (
        <Box sx={{ fontSize: 12.5, color: c.INK_2 }}>
            <Box component="span" sx={{ fontWeight: 700, color: c.INK, fontVariantNumeric: 'tabular-nums' }}>
                {v}
            </Box>{' '}
            {peer != null ? (
                <Box component="span" sx={{ color: c.TEXT_3 }}>
                    {t('agent_perf_peer_max', { n: peer, defaultValue: '(peer max {{n}})' })}
                </Box>
            ) : null}
            <Box component="span" sx={{ color: c.TEXT_3, display: 'block', fontSize: 12 }}>
                {label}
            </Box>
        </Box>
    );
}

// Compact horizontal-bar version of the overall score breakdown, shown
// next to the score itself in the panel header.
function ScoreBreakdownBar({ b }) {
    const { t } = useTranslation();
    const total = SCORE_WEIGHTS.productivity + SCORE_WEIGHTS.engagement + SCORE_WEIGHTS.quality;
    const segs = [
        { key: 'P', label: t('agent_perf_productivity', { defaultValue: 'Productivity' }), v: b.productivity, of: SCORE_WEIGHTS.productivity, color: c.INFO_TEXT },
        { key: 'E', label: t('agent_perf_engagement', { defaultValue: 'Engagement' }), v: b.engagement, of: SCORE_WEIGHTS.engagement, color: c.ACCENT_TEXT },
        { key: 'Q', label: t('agent_perf_quality', { defaultValue: 'Quality' }), v: b.quality, of: SCORE_WEIGHTS.quality, color: c.SUCCESS_TEXT },
    ];
    return (
        <Box>
            <Box sx={{ display: 'flex', height: 20, borderRadius: '10px', overflow: 'hidden', bgcolor: c.SURFACE_ALT }}>
                {segs.map((s) => (
                    <Tooltip key={s.key} title={`${s.label}: ${s.v} / ${s.of}`}>
                        <Box
                            sx={{
                                width: `${(s.of / total) * 100}%`,
                                bgcolor: alpha(s.color, 0.3 + 0.7 * (s.v / s.of)),
                                color: c.WHITE,
                                fontSize: 11,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {s.v}
                        </Box>
                    </Tooltip>
                ))}
            </Box>
            {b.penalty > 0 ? (
                <Typography sx={{ fontSize: 12.5, color: c.DANGER_TEXT, mt: 0.5 }}>
                    {t('agent_perf_penalty_short', { n: b.penalty, defaultValue: '−{{n}} risk penalty' })}
                </Typography>
            ) : null}
        </Box>
    );
}

// 7×24 heatmap of activity intensity. Days run vertically (Mon→Sun),
// hours horizontally (12 am → 11 pm) in Addis Ababa time (EAT, UTC+3).
// Cell strength proportional to event count vs the agent's peak hour.
// Cells inside the core 9:00 am – 5:30 pm window are blue; cells outside it
// are red — instantly reveals an agent working a night shift or a weekend.
function Heatmap({ data }) {
    const { t, i18n } = useTranslation();
    // Reindex to Mon-first. moment().day() returns 0=Sun, 1=Mon… so the
    // map keys are dow*24+hour where dow is the moment day index.
    const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon..Sun
    // Short weekday names in the portal's language (1 Jan 2024 was a Monday).
    const dayName = (i) => {
        try {
            return new Intl.DateTimeFormat(i18n.language || 'en', { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, 1 + i)));
        } catch (e) {
            return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i];
        }
    };

    let max = 0;
    Object.values(data).forEach((v) => {
        if (v > max) max = v;
    });
    const cellSize = 14;

    // Hour-bucket classification. Working = the hour is wholly inside
    // 9:00 am – 5:30 pm (hours 9–16, plus the 5 pm bucket whose first half is
    // within the window). Everything else is off-hours. Minute-precision still
    // applies to the actual risk-flag math; this is just the visual indicator.
    const isWorkingHour = (h) => h >= WORK_START_HOUR && h <= WORK_END_HOUR;

    return (
        <Box sx={{ minWidth: 32 + 24 * (cellSize + 2) }}>
            <Box sx={{ display: 'flex', pl: '40px', mb: 0.5 }}>
                {[0, 6, 12, 18].map((h) => (
                    <Typography key={h} component="span" sx={{ color: c.TEXT_3, fontSize: 11, width: (cellSize + 2) * 6, textAlign: 'start' }}>
                        {hourText(h)}
                    </Typography>
                ))}
            </Box>
            {DOW_ORDER.map((dow, rowIdx) => (
                <Box key={dow} sx={{ display: 'flex', alignItems: 'center', height: cellSize + 2 }}>
                    <Typography component="span" sx={{ width: 40, color: c.INK_2, fontSize: 11, textAlign: 'end', pr: 1, flex: 'none' }}>
                        {dayName(rowIdx)}
                    </Typography>
                    {Array.from({ length: 24 }, (_, hour) => {
                        const v = data[dow * 24 + hour] || 0;
                        const intensity = max > 0 ? v / max : 0;
                        const working = isWorkingHour(hour);
                        const tone = working ? c.INFO_TEXT : c.DANGER_TEXT;
                        return (
                            <Tooltip
                                key={hour}
                                title={t('agent_perf_heat_cell', {
                                    day: dayName(rowIdx),
                                    hour: hourText(hour),
                                    count: v,
                                    defaultValue_one: '{{day}} {{hour}} — {{count}} event',
                                    defaultValue_other: '{{day}} {{hour}} — {{count}} events',
                                    defaultValue: '{{day}} {{hour}} — {{count}} events',
                                })}
                            >
                                <Box
                                    sx={{
                                        width: cellSize,
                                        height: cellSize,
                                        mr: '2px',
                                        flex: 'none',
                                        borderRadius: '2px',
                                        bgcolor: v === 0 ? c.SURFACE_ALT : alpha(tone, 0.15 + intensity * 0.85),
                                        border: v > 0 && !working ? `1px solid ${alpha(c.DANGER_TEXT, 0.4)}` : 'none',
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box aria-hidden="true" sx={{ width: 10, height: 10, bgcolor: alpha(c.INFO_TEXT, 0.7), borderRadius: '2px' }} />
                    <Typography component="span" sx={{ color: c.INK_2, fontSize: 12 }}>
                        {t('agent_perf_core_hours', { from: hourText(WORK_START_HOUR), to: hourText(WORK_END_HOUR, WORK_END_MIN), defaultValue: 'Core hours ({{from}} – {{to}})' })}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box aria-hidden="true" sx={{ width: 10, height: 10, bgcolor: alpha(c.DANGER_TEXT, 0.7), borderRadius: '2px', border: `1px solid ${alpha(c.DANGER_TEXT, 0.4)}` }} />
                    <Typography component="span" sx={{ color: c.INK_2, fontSize: 12 }}>
                        {t('agent_perf_off_hours', { defaultValue: 'Outside core hours' })}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

// Factory for the per-agent aggregate bucket — keeps the shape
// consistent between seeding (from users) and discovery (from data).
function mkAgentBucket(uid, { name, email, type, image }) {
    return {
        uid,
        name, email, type, image,
        calls: {
            total: 0,
            byCategory: {},
            uniqueDrivers: new Set(),
            driverCounts: {},
            list: [],
        },
        activity: {
            total: 0,
            pageViews: 0,
            downloads: 0,
            actions: 0,
            list: [],
        },
        engagement: {
            timeOnPortalMs: 0,
            sessionCount: 0,
            activeDays: 0,
            avgSessionMs: 0,
        },
        quality: {
            uniqueDrivers: 0,
            categoryEntropy: 0,
            repeatDrivers: 0,
            repeatDriverPct: 0,
            sensitiveCases: 0,
        },
        productivity: { calls: 0, drivers: 0, actions: 0, downloads: 0 },
        risk: { flags: [] },
        sessions: {},
        ips: { all: new Set(), byDay: {} },
        activeDays: new Set(),
        offHoursEvents: 0,
        heatmap: {},
        lastSeen: 0,
    };
}
